import { Link, NavLink } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useCartStore } from '../../stores/cartStore';
import { useAuthStore } from '../../stores/authStore';
import { activeSocialLinks } from '../../lib/socialLinks';
import { t } from '../../i18n/pt';
import { Icon } from '../ui/Icon';
import { PixelStartLogo } from '../ui/PixelStartLogo';
import styles from './PublicLayout.module.scss';

interface Props {
  children: ReactNode;
}

export function SiteChrome({ children }: Props): JSX.Element {
  const itemCount = useCartStore((s) => s.getItemCount());
  const user = useAuthStore((s) => s.user);
  const [menuOpen, setMenuOpen] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const navLinks = (
    <>
      <NavLink to="/" end onClick={() => setMenuOpen(false)}>
        {t.nav.homepage}
      </NavLink>
      <NavLink to="/produtos" onClick={() => setMenuOpen(false)}>
        {t.nav.shop}
      </NavLink>
      <NavLink to="/sobre" onClick={() => setMenuOpen(false)}>
        {t.nav.about}
      </NavLink>
    </>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <button
              type="button"
              className={styles.menuToggle}
              aria-label={menuOpen ? t.nav.close : t.nav.menu}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
            </button>
            <nav className={styles.nav} aria-label="Principal">
              {navLinks}
            </nav>
          </div>

          <Link to="/" className={styles.logo} aria-label="Rosita Store — Página inicial">
            <img src="/brand/rosita-logo.webp" alt="Rosita Store" className={styles.logoImg} />
          </Link>

          <div className={styles.actions}>
            <NavLink
              to={user ? '/conta' : '/conta/login'}
              className={styles.iconBtn}
              aria-label={user ? t.nav.account : t.nav.login}
            >
              <span className={styles.userWrap}>
                <Icon name="user" size={20} />
                {user ? <span className={styles.userDot} aria-hidden /> : null}
              </span>
            </NavLink>
            <NavLink
              to="/carrinho"
              className={styles.iconBtn}
              aria-label={`${t.nav.cartAria}, ${itemCount} ${itemCount === 1 ? 'artigo' : 'artigos'}`}
            >
              <span className={styles.bagWrap}>
                <Icon name="bag" size={20} />
                {itemCount > 0 ? <span className={styles.badge}>{itemCount}</span> : null}
              </span>
            </NavLink>
          </div>
        </div>

      </header>

      <div
        className={`${styles.drawerBackdrop} ${menuOpen ? styles.drawerBackdropOpen : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />
      <aside
        id="mobile-nav"
        className={`${styles.drawer} ${menuOpen ? styles.drawerOpen : ''}`}
        aria-label="Principal móvel"
        aria-hidden={!menuOpen}
      >
        <div className={styles.drawerHeader}>
          <Link
            to="/"
            className={styles.drawerLogo}
            aria-label="Rosita Store — Página inicial"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/brand/rosita-logo.webp" alt="Rosita Store" />
          </Link>
          <button
            type="button"
            className={styles.drawerClose}
            aria-label={t.nav.close}
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="close" size={22} />
          </button>
        </div>
        <nav className={styles.drawerNav} aria-label="Principal móvel">
          {navLinks}
        </nav>
        <div className={styles.drawerFooter}>
          <NavLink
            to={user ? '/conta' : '/conta/login'}
            onClick={() => setMenuOpen(false)}
            className={styles.drawerSecondary}
          >
            <Icon name="user" size={18} />
            <span>{user ? t.nav.account : t.nav.login}</span>
          </NavLink>
        </div>
      </aside>

      <main className={styles.main}>{children}</main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div>
            <h4 className="label-eyebrow">{t.footer.helpColTitle}</h4>
            <ul>
              <li>
                <Link to="/termos-e-condicoes">{t.footer.helpLinkTerms}</Link>
              </li>
              <li>
                <Link to="/politica-de-privacidade">{t.footer.helpLinkPrivacy}</Link>
              </li>
              <li>
                <Link to="/entregas-e-devolucoes">{t.footer.helpLinkReturns}</Link>
              </li>
              <li>
                <Link to={user ? '/conta' : '/conta/login'}>
                  {t.footer.helpLinkShipping}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="label-eyebrow">{t.footer.legalColTitle}</h4>
            <ul>
              <li>
                <Link to="/termos-e-condicoes">{t.footer.legalLinkInfo}</Link>
              </li>
              <li>
                <Link to="/termos-e-condicoes">{t.footer.legalLinkSale}</Link>
              </li>
              <li>
                <Link to="/politica-de-privacidade">{t.footer.legalLinkCookies}</Link>
              </li>
              <li>
                <a
                  href="https://www.livroreclamacoes.pt/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.footer.legalLinkComplaints}
                </a>
              </li>
              <li>
                <Link to="/termos-e-condicoes#litigios">{t.footer.legalLinkDisputes}</Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="label-eyebrow">{t.footer.contactColTitle}</h4>
            <p className={styles.contactLead}>{t.footer.contactLead}</p>
            <p className={styles.contactEmail}>
              <a href={`mailto:${t.footer.contactEmail}`}>{t.footer.contactEmail}</a>
            </p>
            <p className={styles.contactReply}>{t.footer.contactReply}</p>
          </div>

          {/* Hidden entirely until the real profile URLs are confirmed — see
              lib/socialLinks.ts. */}
          {activeSocialLinks.length > 0 ? (
            <div>
              <h4 className="label-eyebrow">{t.footer.socialColTitle}</h4>
              <ul>
                {activeSocialLinks.map((link) => (
                  <li key={link.label}>
                    <a href={link.url} target="_blank" rel="noreferrer">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.footerBottomMeta}>
            <p className={styles.copy}>
              © {year} {t.footer.copyright}
            </p>
            <a
              className={styles.credit}
              href="https://www.pixelstart.pt/"
              target="_blank"
              rel="noreferrer"
              aria-label={`${t.footer.credit} ${t.footer.creditAgency}`}
            >
              <span>{t.footer.credit}</span>
              <PixelStartLogo className={styles.creditLogo} />
            </a>
          </div>
          <Link to="/" className={styles.footerWordmark} aria-label="Rosita Store">
            ROSITA <em>Store</em>
          </Link>
        </div>
      </footer>
    </div>
  );
}
